import React, { useState, useEffect } from 'react';
import { useEscapeKey } from '../shared/useGlobalModalEscape';
import { dashboardService } from '../modules/dashboard/dashboardService';
import {
  Calendar as CalendarIcon,
  X,
  ChevronLeft,
  ChevronRight,
  Clock,
  AlertTriangle,
  FileText,
  ShieldAlert,
  GitBranch,
  CheckCircle2,
  ExternalLink,
  Layers,
  ArrowRight
} from 'lucide-react';

export interface PendingTaskItem {
  id: string;
  code: string;
  title: string;
  type: 'REPORT' | 'ACTION' | 'MOC' | 'INVESTIGATION' | 'CAPA' | 'HAZARD';
  targetTab: string;
  dueDate: string; // YYYY-MM-DD
  formattedDate: string;
  daysLeft: number;
  urgency: 'critical' | 'high' | 'medium' | 'normal';
  status: string;
  assignee: string;
}

interface CalendarDueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tab: string) => void;
}

export const CalendarDueModal: React.FC<CalendarDueModalProps> = ({
  isOpen,
  onClose,
  onNavigate
}) => {
  const [currentMonth, setCurrentMonth] = useState<number>(() => new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(() => new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [tasks, setTasks] = useState<PendingTaskItem[]>([]);

  useEffect(() => {
    let isMounted = true;
    dashboardService.getCalendarEvents()
      .then((events) => {
        if (!isMounted) return;
        if (Array.isArray(events) && events.length > 0) {
          const liveItems: PendingTaskItem[] = events.map((ev: any, idx: number) => {
            const rawDue = ev.date || new Date().toISOString().substring(0, 10);
            const now = new Date();
            const target = new Date(rawDue);
            const daysLeft = Math.round((target.getTime() - now.getTime()) / (1000 * 3600 * 24));
            return {
              id: ev.id || `live-${idx}`,
              code: ev.targetId || ev.id || `EV-${idx}`,
              title: ev.title || 'Operational Schedule',
              type: (ev.category?.toUpperCase() || 'ACTION') as any,
              targetTab: ev.targetTab || 'actions',
              dueDate: rawDue,
              formattedDate: rawDue,
              daysLeft: daysLeft,
              urgency: daysLeft <= 2 ? 'critical' : daysLeft <= 5 ? 'high' : 'normal',
              status: ev.status || 'SCHEDULED',
              assignee: 'Safety Department'
            };
          });
          setTasks(liveItems);
        }
      })
      .catch((err) => {
        console.warn('Could not load live calendar events from database:', err);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEscapeKey(onClose, isOpen);

  if (!isOpen) return null;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  // Days in month
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay(); // 0 = Sun

  const filteredTasks = tasks.filter((task) => {
    if (typeFilter !== 'ALL' && task.type !== typeFilter) return false;
    if (selectedDate) {
      return task.dueDate === selectedDate;
    }
    return true;
  });

  const getBadgeClass = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return 'cal-badge-critical';
      case 'high':
        return 'cal-badge-high';
      case 'medium':
        return 'cal-badge-medium';
      default:
        return 'cal-badge-normal';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'REPORT':
        return <FileText size={14} className="text-blue-500" />;
      case 'HAZARD':
        return <AlertTriangle size={14} className="text-amber-500" />;
      case 'MOC':
        return <GitBranch size={14} className="text-purple-500" />;
      case 'INVESTIGATION':
        return <ShieldAlert size={14} className="text-rose-500" />;
      case 'ACTION':
      case 'CAPA':
        return <CheckCircle2 size={14} className="text-emerald-500" />;
      default:
        return <Clock size={14} />;
    }
  };

  return (
    <div className="cal-modal-backdrop" onClick={onClose}>
      <div className="cal-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="cal-modal-header">
          <div className="cal-modal-title-group">
            <div className="cal-modal-icon-wrap">
              <CalendarIcon size={20} color="#0f172a" />
            </div>
            <div>
              <h3 className="cal-modal-title">Due Dates & Work Schedule</h3>
              <p className="cal-modal-subtitle">
                Calendar view of pending MOC changes, safety actions, investigations & reports
              </p>
            </div>
          </div>
          <button className="cal-modal-close-btn" onClick={onClose} aria-label="Close modal">
            <X size={18} />
          </button>
        </div>

        {/* Content Body: Calendar Grid + Due Dates List */}
        <div className="cal-modal-body">
          {/* Left Column: Interactive Mini Calendar */}
          <div className="cal-left-panel">
            <div className="cal-month-nav">
              <span className="cal-current-month">
                {monthNames[currentMonth]} {currentYear}
              </span>
              <div className="cal-nav-buttons">
                <button onClick={handlePrevMonth} className="cal-nav-arrow" title="Previous Month">
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => {
                    setCurrentMonth(9);
                    setCurrentYear(2026);
                    setSelectedDate(null);
                  }}
                  className="cal-today-btn"
                  title="Go to Today"
                >
                  Today
                </button>
                <button onClick={handleNextMonth} className="cal-nav-arrow" title="Next Month">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Weekday headers */}
            <div className="cal-weekdays">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} className="cal-weekday-cell">
                  {day}
                </div>
              ))}
            </div>

            {/* Days grid */}
            <div className="cal-days-grid">
              {Array.from({ length: firstDayIndex }).map((_, i) => (
                <div key={`empty-${i}`} className="cal-day-cell cal-day-empty" />
              ))}

              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                const isToday = currentYear === 2026 && currentMonth === 9 && dayNum === 26;
                const isSelected = selectedDate === dateStr;

                const dayTasks = tasks.filter((t) => t.dueDate === dateStr);
                const hasCritical = dayTasks.some((t) => t.urgency === 'critical');
                const hasHigh = dayTasks.some((t) => t.urgency === 'high');
                const hasMedium = dayTasks.some((t) => t.urgency === 'medium' || t.urgency === 'normal');

                return (
                  <button
                    key={dateStr}
                    type="button"
                    onClick={() => {
                      if (selectedDate === dateStr) {
                        setSelectedDate(null);
                      } else {
                        setSelectedDate(dateStr);
                      }
                    }}
                    className={`cal-day-cell ${isToday ? 'cal-day-today' : ''} ${
                      isSelected ? 'cal-day-selected' : ''
                    } ${dayTasks.length > 0 ? 'cal-day-has-tasks' : ''}`}
                  >
                    <span className="cal-day-number">{dayNum}</span>
                    {dayTasks.length > 0 && (
                      <div className="cal-task-dots">
                        {hasCritical && <span className="cal-dot cal-dot-critical" />}
                        {hasHigh && <span className="cal-dot cal-dot-high" />}
                        {hasMedium && !hasCritical && !hasHigh && (
                          <span className="cal-dot cal-dot-blue" />
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Calendar Legend */}
            <div className="cal-legend">
              <span className="cal-legend-item">
                <span className="cal-dot cal-dot-critical" /> Critical Due (&le; 2d)
              </span>
              <span className="cal-legend-item">
                <span className="cal-dot cal-dot-high" /> Urgent (&le; 5d)
              </span>
              <span className="cal-legend-item">
                <span className="cal-dot cal-dot-blue" /> MOC & Actions
              </span>
            </div>

            {selectedDate && (
              <div className="cal-filter-notice">
                <span>Showing items for: <b>{selectedDate}</b></span>
                <button onClick={() => setSelectedDate(null)} className="cal-filter-clear">
                  Show All Dates
                </button>
              </div>
            )}
          </div>

          {/* Right Column: Pending Due Dates List */}
          <div className="cal-right-panel">
            {/* Filter Tabs */}
            <div className="cal-type-filters">
              {[
                { key: 'ALL', label: 'All Items' },
                { key: 'MOC', label: 'MOC' },
                { key: 'ACTION', label: 'Actions' },
                { key: 'REPORT', label: 'Reports' },
                { key: 'INVESTIGATION', label: 'Investigations' }
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setTypeFilter(tab.key)}
                  className={`cal-filter-pill ${typeFilter === tab.key ? 'active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Task Cards List */}
            <div className="cal-tasks-list">
              {filteredTasks.length === 0 ? (
                <div className="cal-tasks-empty">
                  <Clock size={28} color="#94a3b8" />
                  <p>No pending deadlines found for this selection.</p>
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    className="cal-task-card"
                    onClick={() => {
                      onNavigate(task.targetTab);
                      onClose();
                    }}
                  >
                    <div className="cal-task-card-header">
                      <div className="cal-task-type-badge">
                        {getTypeIcon(task.type)}
                        <span>{task.type}</span>
                      </div>
                      <span className={`cal-due-pill ${getBadgeClass(task.urgency)}`}>
                        {task.daysLeft <= 0 ? 'Due Today' : `${task.daysLeft} days left`}
                      </span>
                    </div>

                    <div className="cal-task-code">{task.code}</div>
                    <div className="cal-task-title">{task.title}</div>

                    <div className="cal-task-card-footer">
                      <div className="cal-task-meta">
                        <CalendarIcon size={12} />
                        <span>Due: {task.formattedDate}</span>
                        <span className="cal-meta-dot">•</span>
                        <span className="cal-task-status">{task.status}</span>
                      </div>
                      <span className="cal-task-go-btn" title="Open Module">
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
